import { get, isNull, isString } from "lodash";
import { ColumnPassThroughOptions } from "primereact/column";
import { Menu } from "primereact/menu";
import { classNames } from "primereact/utils";
import { RefObject, SyntheticEvent, createRef, useRef } from "react";
import {
    ColumnOptions,
    ColumnProps,
    DataTableSettings,
    UpdateDataTableSettingsFunction,
} from "../../../models/table.model";
import MultiSortMenu from "./MultiSortMenu";

interface MultipleRefObject {
    [key: string]: RefObject<Menu | null>;
}

const WithMultiSortMenu = () => {
    const menuRefs = useRef<MultipleRefObject>({});

    const getMenuRefKey = (column: ColumnOptions, columnIndex: number) =>
        column.columnName || `${columnIndex}`;

    const addMultipleSortMenuToColumn = (
        column: ColumnOptions,
        columnIndex: number,
        currentSortType: string | null | undefined,
        currentSortAsc: boolean | null
    ) => {
        const hasColumnMultiSortMenu =
            column.multiSortMenu && column.multiSortMenu.length > 0;

        if (!hasColumnMultiSortMenu) {
            return column;
        }

        // create ref & ref-event for new MultiSortMenu
        const currentRefKey = getMenuRefKey(column, columnIndex);
        menuRefs.current[currentRefKey] = createRef();

        const onClickCellHeader = (
            e: SyntheticEvent<Element, Event>,
            columnName: string
        ) => {
            if (!isNull(menuRefs.current[columnName].current)) {
                // @ts-ignore-next-line
                menuRefs.current[columnName].current.toggle(e);
            }
        };

        const isSortTypeCurrentMultiSortMenu =
            Array.isArray(column.multiSortMenu) &&
            column.multiSortMenu.some(
                (colSortField) => colSortField.field === currentSortType
            );

        const isSortTypeCurrentColumn =
            isSortTypeCurrentMultiSortMenu ||
            currentSortType === column.props.sortField ||
            currentSortType === column.columnName;

        const overrideHeaderProps =
            isSortTypeCurrentColumn && isString(column.props?.header)
                ? {
                      header: (
                          <div className="flex items-center gap-2 space-between whitespace-nowrap">
                              <span>
                                  <>{column.props.header || ""}</>
                              </span>
                              <i
                                  className={classNames("pi", {
                                      "pi-sort-amount-up-alt": currentSortAsc,
                                      "pi-sort-amount-down-alt":
                                          !currentSortAsc,
                                  })}
                                  // @ts-ignore-next-line
                                  width="14"
                                  height="14"
                                  viewBox="0 0 14 14"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  aria-hidden="true"
                                  data-pc-section="sorticon"
                              />
                          </div>
                      ),
                  }
                : {
                      header: column.props.header,
                  };

        return {
            ...column,
            props: {
                ...(column.props || {}),
                sortable: !isSortTypeCurrentColumn,
                ...overrideHeaderProps,

                pt: {
                    ...(column.props.pt || {}),
                    sortField: column.columnName,
                    headerCell: {
                        onClick: (e) => onClickCellHeader(e, currentRefKey),
                        className: classNames("cursor-pointer", {
                            "bg-primary-default/10": isSortTypeCurrentColumn,
                        }),
                        "data-p-sortable-column": "true",
                        ...(isSortTypeCurrentColumn
                            ? {
                                  "aria-sort": currentSortAsc
                                      ? "ascending"
                                      : "descending",
                              }
                            : {}),
                    } as React.HTMLAttributes<HTMLSpanElement>,
                } as ColumnPassThroughOptions,
            } as ColumnProps,
        };
    };

    const WithMultiSortMenu = (
        columns: ColumnOptions[],
        settings: DataTableSettings
    ) => {
        const hasMultiSortMenu = columns.some((c) => c.multiSortMenu);

        const { sortType = null, sortAsc = false } = settings;

        if (!hasMultiSortMenu) {
            return columns.map((col) => ({
                ...col,
                props: {
                    ...(col.props || {}),
                    sortable: get(col, "sortable", false),
                },
            }));
        }

        return columns.map((col, index) =>
            addMultipleSortMenuToColumn(col, index, sortType, sortAsc)
        );
    };

    const MultipleSortMenu = (props: {
        onSortMenuChange: UpdateDataTableSettingsFunction;
        columns: ColumnOptions[];
        sortAsc: boolean;
        sortType: string;
    }) => {
        const {
            onSortMenuChange = () => {},
            columns = [],
            sortAsc = false,
            sortType = "",
        } = props;

        return (
            <>
                {columns
                    .map((column: ColumnOptions, columnIndex: number) => {
                        const hasColumnMultiSortMenu =
                            column.multiSortMenu &&
                            column.multiSortMenu.length > 0;

                        if (!hasColumnMultiSortMenu) {
                            return null;
                        }
                        const menuRefKey = getMenuRefKey(column, columnIndex);
                        return (
                            <MultiSortMenu
                                key={`multisortmenu-${columnIndex}-${menuRefKey}`}
                                {...props}
                                menuRef={menuRefs.current[menuRefKey]}
                                items={column.multiSortMenu || []}
                                onSortMenuClick={(field) =>
                                    onSortMenuChange &&
                                    onSortMenuChange(
                                        (oldSettings: DataTableSettings) => {
                                            const sortDesc =
                                                oldSettings.sortType ===
                                                    field &&
                                                oldSettings.sortAsc;
                                            const doDeleteField =
                                                oldSettings.sortType ===
                                                    field &&
                                                !oldSettings.sortAsc;
                                            return {
                                                ...oldSettings,
                                                sortAsc: doDeleteField
                                                    ? null
                                                    : !sortDesc,
                                                sortType: doDeleteField
                                                    ? null
                                                    : field,
                                            };
                                        }
                                    )
                                }
                                sortAsc={sortAsc}
                                sortType={sortType}
                            />
                        );
                    })
                    .filter((c) => !!c)}
            </>
        );
    };

    return {
        MultipleSortMenu,
        WithMultiSortMenu,
    };
};

export default WithMultiSortMenu;
